import type { DynamicSectionEmittedQuestion } from './aiDynamicSection';
import { VoiceOrTextInput, type VoiceOrTextTranscribeContext } from './VoiceOrTextInput';

/** Everything the `open_text` case needs to render the same voice-or-type experience an ordinary
 *  survey question offers, owned by `AiDynamicSectionRunner` (see its docblock) and threaded
 *  through as one group rather than six separate props. Unused by every other emitted type. */
export interface AiDynamicSectionVoiceProps {
  showTextInput: boolean;
  onShowTextInputChange: (showTextInput: boolean) => void;
  transcriptionUnavailable: boolean;
  onTranscriptionUnavailable: () => void;
  disableVoiceInput: boolean;
  disableTextInput: boolean;
  slotKey: string;
  transcribeContext: VoiceOrTextTranscribeContext;
  onError: (message: string) => void;
  /** Stopping the mic is the submit here too — the runner sends the turn the moment the words
   *  exist, rather than making the respondent press Next after speaking. */
  onSpokenAnswerTranscribed: (fullValue: string) => void;
}

interface AiDynamicSectionQuestionInputProps {
  question: DynamicSectionEmittedQuestion;
  answer: unknown;
  onAnswerChange: (next: unknown) => void;
  /** Required only by the `open_text` case - see `AiDynamicSectionVoiceProps`. */
  voice: AiDynamicSectionVoiceProps;
}

const CHOICE_BUTTON_BASE =
  'w-full text-left px-4 py-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500';
const CHOICE_BUTTON_SELECTED = 'border-blue-500 bg-blue-50 text-blue-700';
const CHOICE_BUTTON_UNSELECTED = 'border-gray-300 hover:border-gray-400 text-gray-900';

/**
 * Renders one emitted dynamic-section turn with the same six input shapes an authored question
 * of that type would use (spec-01: "rendered exactly like an authored question of the same
 * type"). Kept as a small, self-contained widget set for five of the six - `SurveySession.tsx`'s
 * own per-type rendering lives inside its 6,000+-line `renderQuestionInput` closure, coupled to
 * that component's local hooks (media-load skeleton state, MaxDiff cursors, etc.) and not
 * extractable without pulling that whole component in.
 *
 * `open_text` is the exception: its voice-or-type experience (task-9, "shall work as other
 * questions work in survey") is the ONE piece genuinely shared with `SurveySession.tsx`, via the
 * extracted `VoiceOrTextInput` - see that component's docblock for why the mic lifecycle lives in
 * exactly one place rather than being copied here.
 */
export function AiDynamicSectionQuestionInput({
  question,
  answer,
  onAnswerChange,
  voice,
}: AiDynamicSectionQuestionInputProps) {
  switch (question.type) {
    case 'open_text':
      return (
        <VoiceOrTextInput
          value={typeof answer === 'string' ? answer : ''}
          onChange={onAnswerChange}
          showTextInput={voice.showTextInput}
          onShowTextInputChange={voice.onShowTextInputChange}
          transcriptionUnavailable={voice.transcriptionUnavailable}
          onTranscriptionUnavailable={voice.onTranscriptionUnavailable}
          disableVoiceInput={voice.disableVoiceInput}
          disableTextInput={voice.disableTextInput}
          slotKey={voice.slotKey}
          transcribeContext={voice.transcribeContext}
          onError={voice.onError}
          onSpokenAnswerTranscribed={voice.onSpokenAnswerTranscribed}
        />
      );

    case 'single_choice':
      return (
        <div className="space-y-2">
          {(question.options ?? []).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onAnswerChange(option)}
              className={`${CHOICE_BUTTON_BASE} ${
                answer === option ? CHOICE_BUTTON_SELECTED : CHOICE_BUTTON_UNSELECTED
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      );

    case 'multiple_choice': {
      const selected = Array.isArray(answer) ? (answer as string[]) : [];
      const toggle = (option: string) =>
        onAnswerChange(
          selected.includes(option) ? selected.filter((o) => o !== option) : [...selected, option],
        );
      return (
        <div className="space-y-2">
          {(question.options ?? []).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => toggle(option)}
              className={`${CHOICE_BUTTON_BASE} ${
                selected.includes(option) ? CHOICE_BUTTON_SELECTED : CHOICE_BUTTON_UNSELECTED
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      );
    }

    case 'rating_scale': {
      const scaleMin = (question.type_config?.scale_min as number | undefined) ?? 1;
      const scaleMax = (question.type_config?.scale_max as number | undefined) ?? 5;
      const scale = Array.from({ length: scaleMax - scaleMin + 1 }, (_, i) => scaleMin + i);
      return (
        <div className="flex flex-wrap gap-2">
          {scale.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => onAnswerChange(value)}
              className={`w-12 h-12 rounded-full border font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                answer === value ? CHOICE_BUTTON_SELECTED : CHOICE_BUTTON_UNSELECTED
              }`}
            >
              {value}
            </button>
          ))}
        </div>
      );
    }

    case 'matrix_rating': {
      const rows = (question.type_config?.rows as string[] | undefined) ?? [];
      const columns = (question.type_config?.columns as string[] | undefined) ?? [];
      const given = (typeof answer === 'object' && answer !== null ? answer : {}) as Record<
        string,
        string
      >;
      const setRow = (row: string, column: string) => onAnswerChange({ ...given, [row]: column });
      return (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left p-2" />
                {columns.map((column) => (
                  <th key={column} className="text-center p-2 font-medium text-gray-700">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row} className="border-t border-gray-200">
                  <td className="p-2 text-gray-900">{row}</td>
                  {columns.map((column) => (
                    <td key={column} className="text-center p-2">
                      <button
                        type="button"
                        onClick={() => setRow(row, column)}
                        aria-label={`${row}: ${column}`}
                        className={`w-6 h-6 rounded-full border ${
                          given[row] === column
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case 'percentage_allocation': {
      const options = (question.type_config?.options as string[] | undefined) ?? [];
      const targetSum = (question.type_config?.target_sum as number | undefined) ?? 100;
      const given = (typeof answer === 'object' && answer !== null ? answer : {}) as Record<
        string,
        number
      >;
      const total = options.reduce((sum, option) => sum + (given[option] || 0), 0);
      const setValue = (option: string, value: number) =>
        onAnswerChange({ ...given, [option]: value });
      return (
        <div className="space-y-3">
          {options.map((option) => (
            <div key={option} className="flex items-center justify-between gap-4">
              <label htmlFor={`alloc-${option}`} className="text-gray-900">
                {option}
              </label>
              <input
                id={`alloc-${option}`}
                type="number"
                min={0}
                max={targetSum}
                value={given[option] ?? ''}
                onChange={(e) => setValue(option, Number(e.target.value) || 0)}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-right"
              />
            </div>
          ))}
          <p className={`text-sm ${total === targetSum ? 'text-green-600' : 'text-gray-500'}`}>
            Total: {total} / {targetSum}
          </p>
        </div>
      );
    }

    default:
      // A dynamic section never emits anything outside the six allowed types - the backend
      // discards and re-asks otherwise (spec-01 edge case).
      return null;
  }
}
